// Nested App Authentication (NAA) auth path.
//
// NAA runs MSAL *inside the task pane iframe* and brokers token requests
// through the Office host (Outlook), instead of the Dialog API popup +
// messageParent round-trip used by auth.ts. Because brokering goes through
// the host rather than the iframe's own (partitioned) storage, NAA sidesteps
// the third-party storage partitioning the dialog flow works around — which
// is what makes silent token renewal reliable here.
//
// This is the PRIMARY auth path on hosts that support the NestedAppAuth 1.1
// requirement set. taskpane.tsx falls back to the Dialog flow (auth.ts) on
// hosts that don't (older desktop builds, etc.).
//
// Since the add-in is only usable from the wongrat.com mailbox (it's grayed
// out in the work account), the Office host identity is always wongrat.com
// when this runs — so NAA's native silent SSO yields exactly the identity we
// need, with no account-forcing required.

/* global Office, console, atob */

import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
  type IPublicClientApplication,
  type SilentRequest,
} from "@azure/msal-browser";
import { naaMsalConfig, epicScopes, graphScopes } from "./config";

/** Session shape returned to the task pane. Mirrors the dialog flow's payload
 *  so both auth paths feed the same React state. */
export interface NaaSession {
  username: string;
  accessToken: string;
  expiresOn: string | null;
  graphAccessToken: string | null;
  graphExpiresOn: string | null;
}

let pca: IPublicClientApplication | undefined;

/** True when the host supports the NestedAppAuth requirement set. Declaring
 *  it in the manifest isn't supported for Outlook, so this is a runtime check. */
export function isNaaSupported(): boolean {
  try {
    return Office.context.requirements.isSetSupported("NestedAppAuth", "1.1");
  } catch {
    return false;
  }
}

async function getPca(): Promise<IPublicClientApplication> {
  if (!pca) {
    pca = await createNestablePublicClientApplication(naaMsalConfig);
  }
  return pca;
}

function currentAccount(msal: IPublicClientApplication): AccountInfo | undefined {
  return msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? undefined;
}

/**
 * "Does this error mean an interactive sign-in is required?" Checks the MSAL
 * error class AND the underlying error codes, so it holds even if the broker
 * surfaces a differently-typed error. Used to decide popup-vs-rethrow here and
 * downgrade-vs-clear in the task pane.
 */
export function isInteractionRequired(e: unknown): boolean {
  if (e instanceof InteractionRequiredAuthError) return true;
  const code = (e as { errorCode?: string } | null)?.errorCode;
  return (
    code === "interaction_required" || code === "consent_required" || code === "login_required"
  );
}

/**
 * Acquire a token for `scopes`. Tries silent first (brokered through the host
 * — no UI). If interaction is required and `interactive` is set, falls back to
 * a brokered popup. Re-throws otherwise so the caller can decide. `forceRefresh`
 * bypasses MSAL's cached access token (used on a 401, where the cached token was
 * just rejected and returning it again would loop).
 */
async function acquire(
  msal: IPublicClientApplication,
  scopes: string[],
  interactive: boolean,
  forceRefresh: boolean
): Promise<AuthenticationResult> {
  const account = currentAccount(msal);
  const silentRequest: SilentRequest = account
    ? { scopes, account, forceRefresh }
    : { scopes, forceRefresh };
  try {
    return await msal.acquireTokenSilent(silentRequest);
  } catch (e) {
    if (interactive && isInteractionRequired(e)) {
      return await msal.acquireTokenPopup({ scopes });
    }
    throw e;
  }
}

/** Decode a JWT payload without verifying — client-side diagnostics only. */
function decodeClaims(token: string): { iss?: string; aud?: string; ver?: string } {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return {};
  }
}

/**
 * Sign in via NAA. Acquires the EPIC API token (required) and the Graph token
 * (best-effort, for SharePoint headshots). `interactive: false` is a pure
 * silent acquisition (used on load); `true` may surface a brokered popup (the
 * explicit Sign in button and 401 recovery). `forceRefresh` bypasses the cached
 * access token — set it on a 401 refresh so a rejected token isn't re-returned.
 */
export async function naaSignIn(interactive: boolean, forceRefresh = false): Promise<NaaSession> {
  const msal = await getPca();

  const epic = await acquire(msal, epicScopes, interactive, forceRefresh);

  // One-time diagnostic for the make-or-break compatibility check: the EPIC
  // backend validates v1 tokens only (iss = https://sts.windows.net/<tid>/).
  // Surface the issuer so a v2 token — which Laravel would reject — is obvious
  // during testing rather than showing up as an opaque 401.
  const claims = decodeClaims(epic.accessToken);
  const isV1 = typeof claims.iss === "string" && claims.iss.startsWith("https://sts.windows.net/");
  if (isV1) {
    console.info(`[naa] EPIC token OK — v1, iss=${claims.iss} aud=${claims.aud}`);
  } else {
    console.error(
      `[naa] EPIC token is NOT v1 (iss=${claims.iss} aud=${claims.aud} ver=${claims.ver}). ` +
        `Laravel pins iss=sts.windows.net and will reject this — set the app reg's ` +
        `requestedAccessTokenVersion to 1, or update the backend to accept v2.`
    );
  }

  let graphAccessToken: string | null = null;
  let graphExpiresOn: string | null = null;
  try {
    const graph = await acquire(msal, graphScopes, interactive, forceRefresh);
    graphAccessToken = graph.accessToken;
    graphExpiresOn = graph.expiresOn?.toISOString() ?? null;
  } catch (e) {
    // Graph is non-critical (headshots only) — never block EPIC sign-in on it.
    console.warn("[naa] Graph token acquisition failed; headshots disabled:", e);
  }

  return {
    username: epic.account?.username ?? currentAccount(msal)?.username ?? "",
    accessToken: epic.accessToken,
    expiresOn: epic.expiresOn?.toISOString() ?? null,
    graphAccessToken,
    graphExpiresOn,
  };
}

/**
 * Best-effort local sign-out. NAA doesn't support MSAL's logout methods, so we
 * clear the token cache; the Outlook host identity itself is unaffected (the
 * next Sign in re-acquires silently).
 */
export async function naaSignOut(): Promise<void> {
  try {
    await pca?.clearCache();
  } catch (e) {
    console.warn("[naa] clearCache failed:", e);
  }
}
